<script setup lang="ts">
/**
 * Rahmen um alle Screens: Navigation, Sync-Status, Abmelden.
 *
 * Die Navigation ist bewusst flach und ohne Untermenues — bei zwei Nutzern
 * kostet jede Hierarchieebene taeglich Zeit und bringt nichts.
 */
import { ref } from "vue";
import { callTriggerSync, signOutNow } from "@/firebase";
import { relativeTime } from "@/lib/format";
import { useDataStore } from "@/stores/data";

const store = useDataStore();
const syncing = ref(false);
const syncError = ref<string | null>(null);

interface NavItem { to: string; label: string; badge?: "inbox" }

const NAV: NavItem[] = [
  { to: "/", label: "Übersicht" },
  { to: "/abgleich", label: "Abgleich", badge: "inbox" },
  { to: "/planung", label: "Planung" },
  { to: "/forderungen", label: "Offene Posten" },
  { to: "/einstellungen", label: "Einstellungen" },
];

async function sync(): Promise<void> {
  syncing.value = true;
  syncError.value = null;
  try {
    await callTriggerSync();
  } catch (err) {
    syncError.value = (err as Error).message;
  } finally {
    syncing.value = false;
  }
}
</script>

<template>
  <div class="min-h-dvh">
    <header class="border-b border-hairline bg-surface">
      <div class="mx-auto flex max-w-[1280px] flex-wrap items-center gap-x-6 gap-y-3 px-6 py-3">
        <h1 class="text-[17px] font-semibold tracking-tight">STARTS Liquidität</h1>

        <nav class="flex flex-wrap gap-1" aria-label="Bereiche">
          <RouterLink
            v-for="item in NAV" :key="item.to" :to="item.to"
            class="relative rounded-md px-3 py-1.5 text-[13px] font-medium text-ink-2 hover:bg-sunk"
            active-class="!bg-sunk !text-ink"
          >
            {{ item.label }}
            <span
              v-if="item.badge === 'inbox' && store.inbox.length"
              class="num ml-1.5 rounded-full bg-s1 px-1.5 py-0.5 text-[11px] font-semibold text-white"
            >{{ store.inbox.length }}</span>
          </RouterLink>
        </nav>

        <div class="ml-auto flex items-center gap-3 text-[12.5px] text-ink-2">
          <span
            class="size-[7px] shrink-0 rounded-full"
            :style="{ background: store.needsAttention.length ? 'var(--warning)' : 'var(--good)' }"
            aria-hidden="true"
          />
          <span>{{ store.lastSync ? relativeTime(store.lastSync.finishedAt) : "noch kein Abgleich" }}</span>
          <button
            type="button" class="rounded-md border border-hairline px-3 py-1.5 font-medium hover:bg-sunk disabled:opacity-50"
            :disabled="syncing" @click="sync"
          >
            {{ syncing ? "Läuft…" : "Synchronisieren" }}
          </button>
          <button type="button" class="text-ink-3 hover:text-ink-2" @click="signOutNow()">Abmelden</button>
        </div>
      </div>
    </header>

    <div v-if="syncError || store.needsAttention.length" class="mx-auto max-w-[1280px] px-6 pt-4">
      <div class="rounded-lg border border-hairline bg-surface p-3 text-[12.5px]" style="border-left: 3px solid var(--warning)">
        <p v-if="syncError" class="text-ink-2">Synchronisieren fehlgeschlagen: {{ syncError }}</p>
        <p v-for="note in store.needsAttention" :key="note" class="text-ink-2">{{ note }}</p>
      </div>
    </div>

    <main class="mx-auto max-w-[1280px] px-6 py-6">
      <p v-if="store.loading" class="py-16 text-center text-ink-3">Daten werden geladen…</p>
      <RouterView v-else />
    </main>
  </div>
</template>
